import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import BentoDashboard from "@/components/BentoDashboard";
import TextScramble from "@/components/TextScramble";
import { fetchPage } from "@/lib/api";

export default function HomePage() {
  const [loading, setLoading] = useState(true);
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: "-60px" });

  useEffect(() => {
    fetchPage("home")
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page" className="bg-surface">
      {/* Hero with GSAP pinned scroll */}
      <Hero />

      {/* Bento Feature Grid */}
      <BentoDashboard loading={loading} />

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
        data-testid="cta-section"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-indigo-600/[0.08] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-violet-500/[0.06] rounded-full blur-[100px]" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-12 md:p-16 text-center"
        >
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/5 to-transparent pointer-events-none" />
          <div className="relative">
            <h2
              className="font-heading font-bold text-white mb-6"
              style={{ fontSize: "clamp(1.6rem, 3vw + 0.3rem, 2.8rem)" }}
            >
              <TextScramble
                text="Ready to Transform Your Business with AI?"
                trigger={ctaInView}
                speed={20}
              />
            </h2>
            <p className="text-slate-400 text-base md:text-lg mb-10 max-w-2xl mx-auto">
              No jargon. No hard selling. Just a straightforward conversation
              about what's possible.
            </p>
            <Link
              to="/contact"
              data-testid="cta-get-in-touch"
              data-magnetic
              className="group inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-500 rounded-full px-8 py-4 font-semibold transition-all shadow-lg shadow-indigo-600/25 active:scale-95"
            >
              Start the Conversation
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
